export class StudioThemeRuntimeError extends Error {
  constructor(
    message,
    code = "INVALID_STUDIO_THEME_RUNTIME",
  ) {
    super(message);

    this.name = "StudioThemeRuntimeError";
    this.code = code;
  }
}


function isNonEmptyString(value) {
  return (
    typeof value === "string"
    && value.trim().length > 0
  );
}


function isThemeRegistry(value) {
  return Boolean(
    value
    && typeof value === "object"
    && typeof value.get === "function"
    && typeof value.has === "function"
  );
}


function assertThemeRegistry(registry) {
  if (!isThemeRegistry(registry)) {
    throw new StudioThemeRuntimeError(
      "StudioThemeRuntime requires a valid Theme Registry.",
      "INVALID_STUDIO_THEME_REGISTRY",
    );
  }

  return registry;
}


function normalizeOptionalThemeId(themeId) {
  if (themeId === null || themeId === undefined) {
    return null;
  }

  if (!isNonEmptyString(themeId)) {
    throw new StudioThemeRuntimeError(
      "Theme id must be a non-empty string.",
      "INVALID_STUDIO_THEME_ID",
    );
  }

  return themeId.trim();
}


export function createStudioThemeRuntime({
  registry,
  themeId = null,
} = {}) {
  const themeRegistry = assertThemeRegistry(registry);

  let currentThemeId = null;
  const listeners = new Set();

  function notify() {
    for (const listener of Array.from(listeners)) {
      listener();
    }
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new StudioThemeRuntimeError(
        "subscribe() requires a listener function.",
        "INVALID_STUDIO_THEME_LISTENER",
      );
    }

    listeners.add(listener);

    return function unsubscribe() {
      return listeners.delete(listener);
    };
  }

  function setTheme(nextThemeId) {
    const normalizedThemeId =
      normalizeOptionalThemeId(nextThemeId);

    if (normalizedThemeId === null) {
      throw new StudioThemeRuntimeError(
        "setTheme() requires a Theme identifier.",
        "INVALID_STUDIO_THEME_ID",
      );
    }

    const theme = themeRegistry.get(
      normalizedThemeId,
    );

    if (!theme) {
      throw new StudioThemeRuntimeError(
        `StudioTheme "${normalizedThemeId}" is not registered.`,
        "UNKNOWN_STUDIO_THEME_ID",
      );
    }

    const hasChanged = currentThemeId !== normalizedThemeId;

    currentThemeId = normalizedThemeId;

    if (hasChanged) {
      notify();
    }

    return theme;
  }

  function getTheme() {
    if (currentThemeId === null) {
      return null;
    }

    return themeRegistry.get(currentThemeId);
  }

  function getThemeId() {
    return currentThemeId;
  }

  function clear() {
    const previousTheme = getTheme();
    const hasChanged = currentThemeId !== null;

    currentThemeId = null;

    if (hasChanged) {
      notify();
    }

    return previousTheme;
  }

  const runtime = Object.freeze({
    setTheme,
    getTheme,
    getThemeId,
    clear,
    subscribe,
  });

  if (themeId !== null && themeId !== undefined) {
    setTheme(themeId);
  }

  return runtime;
}


export function isStudioThemeRuntime(value) {
  return Boolean(
    value
    && typeof value === "object"
    && typeof value.setTheme === "function"
    && typeof value.getTheme === "function"
    && typeof value.getThemeId === "function"
    && typeof value.clear === "function"
    && typeof value.subscribe === "function"
  );
}


export function assertStudioThemeRuntime(value) {
  if (!isStudioThemeRuntime(value)) {
    throw new StudioThemeRuntimeError(
      "Invalid StudioThemeRuntime contract.",
      "INVALID_STUDIO_THEME_RUNTIME",
    );
  }

  return value;
}
